import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { getPositionSideFromOpenType, getStrategyLegSemantics } from '@einhaus/project314-shared';
import type { BarDuration, RegimeModelVersionOverrides, SessionTimingPolicyByDuration } from '@einhaus/project314-types';
import type { TradeLogicAttachmentReturnState } from '@/features/tradeLogicAuthoring/tradeLogicNavigation';
import type { AppRouter, TradingStrategy, TradingStrategyLeg } from '@/types/appRouter';
import { normalizeRegimeModelVersionOverrides } from './regimePinsPresentation';

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;

export type CreateTradingStrategyInput = RouterInputs['strategy']['createStrategy'];
export type UpdateTradingStrategyInput = RouterInputs['strategy']['updateStrategy'];
export type CreateTradingStrategyLegInput = RouterInputs['strategy']['createStrategyLeg'];
export type UpdateTradingStrategyLegInput = RouterInputs['strategy']['updateStrategyLeg'];
export type DeleteTradingStrategyLegInput = RouterInputs['strategy']['deleteStrategyLeg'];
type CreateTradingStrategyLegOutput = RouterOutputs['strategy']['createStrategyLeg'];
type UpdateTradingStrategyLegOutput = RouterOutputs['strategy']['updateStrategyLeg'];
type DeleteTradingStrategyLegOutput = RouterOutputs['strategy']['deleteStrategyLeg'];
export type TradingStrategyFormMode = 'copy' | 'edit';
export type TradingStrategyFormSource = Pick<
    TradingStrategy,
    | 'active'
    | 'afterHoursRemoved'
    | 'allowedUnderlyingEntityTypes'
    | 'autoBacktest'
    | 'autoBacktestOptionBuyingStrategiesOverride'
    | 'description'
    | 'duration'
    | 'entityListId'
    | 'legs'
    | 'name'
    | 'regimeModelVersionOverrides'
    | 'sessionTimingPolicy'
    | 'tradeLogicV2EntryVersionId'
    | 'tradeLogicV2ExitVersionId'
>;

export interface TradingStrategyFormData {
    name: string;
    description: string;
    active: boolean;
    autoBacktest: boolean;
    autoBacktestOptionBuyingStrategiesOverride?: number[];
    allowedUnderlyingEntityTypes: ('Asset' | 'Cryptocurrency' | 'AssetIndex' | 'FuturesProduct')[];
    duration: BarDuration;
    afterHoursRemoved: boolean;
    entityListId: number | null;
    entryLogicDefinitionId: number | null;
    entryLogicVersion: number | null;
    tradeLogicV2EntryVersionId: number | null;
    exitLogicDefinitionId: number | null;
    exitLogicVersion: number | null;
    tradeLogicV2ExitVersionId: number | null;
    sessionTimingPolicy: SessionTimingPolicyByDuration | null;
    /** Regime model pins, always normalized: `null` for no pins, never `{}` or an empty scope. */
    regimeModelVersionOverrides: RegimeModelVersionOverrides | null;
    legs: TradingStrategyLegFormData[];
}

export interface TradingStrategyAttachmentFormChange {
    definitionId: number | null;
    version: number | null;
    versionId: number | null;
}

export interface TradingStrategyLegFormData {
    id?: number;
    name: string;
    description: string;
    optionType: 'call' | 'put' | null;
    derivativeType: 'Option' | 'Future' | null;
    openTransactionType: 'buyToOpen' | 'sellToOpen' | 'buy' | 'shortSell';
    entryTransactionSizeType: 'percentageValue' | 'percentageRiskCapital' | 'percentageRiskToStop';
    entryTransactionSizeValue: number;
    /** Risk-to-stop sizing: the entry capture holding the protective stop. */
    riskStopCaptureKey: string;
    /** Risk-to-stop sizing: fewer whole units than this skip the entry. */
    minimumQuantity: number | null;
    autoBacktestOptionBuyingStrategiesOverride?: number[];
    isNew?: boolean;
    isModified?: boolean;
    isDeleted?: boolean;
}

export type TradingStrategyFormDataWithRequiredTradeLogicAttachments = TradingStrategyFormData & {
    tradeLogicV2EntryVersionId: number;
    tradeLogicV2ExitVersionId: number;
};

export type TradingStrategyDuration = TradingStrategyFormData['duration'];
export type TradingStrategyLegDerivativeType = TradingStrategyLegFormData['derivativeType'];
export type TradingStrategyLegEntryTransactionSizeType = TradingStrategyLegFormData['entryTransactionSizeType'];
export type TradingStrategyLegOpenTransactionType = TradingStrategyLegFormData['openTransactionType'];

export interface TradingStrategyLegMutationInputs {
    creations: CreateTradingStrategyLegInput[];
    deletions: DeleteTradingStrategyLegInput[];
    updates: UpdateTradingStrategyLegInput[];
}

export interface TradingStrategyLegMutationHandlers {
    createLeg: (input: CreateTradingStrategyLegInput) => Promise<CreateTradingStrategyLegOutput>;
    deleteLeg: (input: DeleteTradingStrategyLegInput) => Promise<DeleteTradingStrategyLegOutput>;
    updateLeg: (input: UpdateTradingStrategyLegInput) => Promise<UpdateTradingStrategyLegOutput>;
}

type StrategyLegOpenTradeType = CreateTradingStrategyLegInput['openTradeType'];

interface StrategyLegMutationFields {
    autoBacktestOptionBuyingStrategiesOverride: number[] | undefined;
    derivativeType: 'Option' | 'Future' | null;
    description: string;
    optionType: 'call' | 'put' | null;
    entryTransactionSizeType: 'percentageValue' | 'percentageRiskCapital' | 'percentageRiskToStop';
    entryTransactionSizeValue: number;
    name: string;
    number: number;
    openTradeType: StrategyLegOpenTradeType;
}

interface RiskToStopSizingFields {
    riskStopCaptureKey: string;
    minimumQuantity: number | null;
}

/** The strategy facts that decide which size types a leg may use. */
export type TradingStrategyLegSizingScope = Pick<TradingStrategyFormData, 'allowedUnderlyingEntityTypes' | 'duration'>;

/** Native futures research runs on 1m bars or 5m price entries; the backend admits no other futures duration. */
export const FUTURES_STRATEGY_DURATIONS = ['1m', '5m'] as const satisfies readonly TradingStrategyFormData['duration'][];

export const isFuturesStrategyDuration = (duration: TradingStrategyFormData['duration']): boolean =>
    FUTURES_STRATEGY_DURATIONS.some((futuresDuration) => futuresDuration === duration);

/**
 * Mirrors the backend admission: risk-to-stop sizing reads the stop captured by native 5m futures
 * price entries, so only a Future leg on a product-only 5m futures strategy may use it. Every other
 * sizing surface (stock, option, crypto, routed ETF, live) refuses it.
 */
export const isRiskToStopSizingEligible = (
    strategy: TradingStrategyLegSizingScope,
    leg: Pick<TradingStrategyLegFormData, 'derivativeType'>
): boolean =>
    strategy.duration === '5m' &&
    strategy.allowedUnderlyingEntityTypes.length === 1 &&
    strategy.allowedUnderlyingEntityTypes[0] === 'FuturesProduct' &&
    leg.derivativeType === 'Future';

export const hasRequiredTradeLogicV2Attachments = (
    formData: TradingStrategyFormData
): formData is TradingStrategyFormDataWithRequiredTradeLogicAttachments =>
    formData.tradeLogicV2EntryVersionId !== null && formData.tradeLogicV2ExitVersionId !== null;

export const hasRequiredTradeLogicV2EntryAttachment = hasRequiredTradeLogicV2Attachments;

export const isVisibleTradingStrategyLeg = (leg: TradingStrategyLegFormData): boolean => leg.isDeleted !== true;

export const applyEntryTradeLogicAttachmentChange = (
    formData: TradingStrategyFormData,
    attachment: TradingStrategyAttachmentFormChange
): TradingStrategyFormData => ({
    ...formData,
    entryLogicDefinitionId: attachment.definitionId,
    entryLogicVersion: attachment.version,
    tradeLogicV2EntryVersionId: attachment.versionId
});

export const applyExitTradeLogicAttachmentChange = (
    formData: TradingStrategyFormData,
    attachment: TradingStrategyAttachmentFormChange
): TradingStrategyFormData => ({
    ...formData,
    exitLogicDefinitionId: attachment.definitionId,
    exitLogicVersion: attachment.version,
    tradeLogicV2ExitVersionId: attachment.versionId
});

export const hasVisibleTradingStrategyOptionLeg = (legs: TradingStrategyLegFormData[]): boolean =>
    legs.some((leg) => isVisibleTradingStrategyLeg(leg) && leg.derivativeType === 'Option');

export const getNextTradingStrategyLegName = (legs: TradingStrategyLegFormData[]): string =>
    `Leg ${legs.filter(isVisibleTradingStrategyLeg).length + 1}`;

export const buildTradingStrategyFormDataFromStrategy = (
    strategy: TradingStrategyFormSource,
    mode: TradingStrategyFormMode,
    attachment?: TradeLogicAttachmentReturnState | null
): TradingStrategyFormData => {
    const baseFormData: TradingStrategyFormData = {
        active: mode === 'copy' ? false : strategy.active,
        afterHoursRemoved: strategy.afterHoursRemoved,
        allowedUnderlyingEntityTypes: toAllowedStrategyEntityTypes(strategy.allowedUnderlyingEntityTypes),
        autoBacktest: strategy.autoBacktest,
        autoBacktestOptionBuyingStrategiesOverride: strategy.autoBacktestOptionBuyingStrategiesOverride || undefined,
        description: strategy.description,
        duration: strategy.duration,
        entityListId: strategy.entityListId ?? null,
        legs: strategy.legs.map((leg) => buildTradingStrategyLegFormDataFromStrategy(leg, mode)),
        name: mode === 'copy' ? `${strategy.name} (Copy)` : strategy.name,
        regimeModelVersionOverrides: normalizeRegimeModelVersionOverrides(strategy.regimeModelVersionOverrides),
        sessionTimingPolicy: strategy.sessionTimingPolicy || null,
        entryLogicDefinitionId: null,
        entryLogicVersion: null,
        tradeLogicV2EntryVersionId: strategy.tradeLogicV2EntryVersionId ?? null,
        exitLogicDefinitionId: null,
        exitLogicVersion: null,
        tradeLogicV2ExitVersionId: strategy.tradeLogicV2ExitVersionId ?? null
    };

    if (!attachment) {
        return baseFormData;
    }

    return attachment.kind === 'entry'
        ? applyEntryTradeLogicAttachmentChange(baseFormData, attachment)
        : applyExitTradeLogicAttachmentChange(baseFormData, attachment);
};

export const buildCreateTradingStrategyInput = (
    formData: TradingStrategyFormDataWithRequiredTradeLogicAttachments
): CreateTradingStrategyInput => ({
    active: formData.active,
    afterHoursRemoved: formData.afterHoursRemoved,
    allowedUnderlyingEntityTypes: formData.allowedUnderlyingEntityTypes,
    autoBacktest: formData.autoBacktest,
    autoBacktestOptionBuyingStrategiesOverride: formData.autoBacktestOptionBuyingStrategiesOverride,
    description: formData.description,
    duration: formData.duration,
    entityListId: formData.entityListId,
    name: formData.name,
    // Copy mode carries the source strategy's pins so the copy resolves the same classifiers.
    regimeModelVersionOverrides: formData.regimeModelVersionOverrides,
    sessionTimingPolicy: formData.sessionTimingPolicy ?? null,
    tradeLogicV2EntryVersionId: formData.tradeLogicV2EntryVersionId,
    tradeLogicV2ExitVersionId: formData.tradeLogicV2ExitVersionId
});

/**
 * `regimeModelVersionOverrides` rides an update only when the user actually changed the pins. The
 * backend leaves an absent field unchanged, while sending it every save would let a key-order
 * difference against the stored JSON masquerade as a runtime-driving change and drag unrelated saves
 * of active strategies through the stricter live-readiness checks.
 */
export const buildUpdateTradingStrategyInput = (
    strategyId: number,
    formData: TradingStrategyFormDataWithRequiredTradeLogicAttachments,
    options: { includeRegimeModelVersionOverrides: boolean }
): UpdateTradingStrategyInput => {
    const { regimeModelVersionOverrides, ...createInput } = buildCreateTradingStrategyInput(formData);

    return {
        id: strategyId,
        ...createInput,
        ...(options.includeRegimeModelVersionOverrides ? { regimeModelVersionOverrides } : {})
    };
};

export const buildTradingStrategyLegMutationInputs = (
    formData: TradingStrategyFormData,
    strategyId: number
): TradingStrategyLegMutationInputs => {
    const visibleLegs = formData.legs.filter(isVisibleTradingStrategyLeg);
    const visibleLegNumberByLeg = new Map(visibleLegs.map((leg, index) => [leg, index + 1]));

    return {
        creations: formData.legs.flatMap((leg) => {
            if (!leg.isNew || leg.isDeleted) return [];

            return [buildCreateTradingStrategyLegInput(formData, strategyId, leg, visibleLegNumberByLeg.get(leg) ?? 1)];
        }),
        deletions: formData.legs.flatMap((leg) => {
            if (leg.id === undefined || !leg.isDeleted) return [];

            return [{ id: leg.id }];
        }),
        updates: formData.legs.flatMap((leg) => {
            if (leg.id === undefined || !leg.isModified || leg.isDeleted) return [];

            return [
                {
                    id: leg.id,
                    ...buildUpdateTradingStrategyLegInput(leg, visibleLegNumberByLeg.get(leg) ?? 1)
                }
            ];
        })
    };
};

export const executeTradingStrategyLegMutations = async (
    inputs: TradingStrategyLegMutationInputs,
    handlers: TradingStrategyLegMutationHandlers
): Promise<void> => {
    await Promise.all(inputs.deletions.map((input) => handlers.deleteLeg(input)));

    await Promise.all([
        ...inputs.updates.map((input) => handlers.updateLeg(input)),
        ...inputs.creations.map((input) => handlers.createLeg(input))
    ]);
};

export const parseTradingStrategyDurationInput = (value: string, fallback: TradingStrategyDuration): TradingStrategyDuration => {
    switch (value) {
        case '1m':
        case '3m':
        case '5m':
        case '15m':
        case '30m':
        case '60m':
        case '120m':
        case '180m':
        case '240m':
        case '1d':
        case '1w':
        case '1mo':
            return value;
        default:
            return fallback;
    }
};

export const parseTradingStrategyLegDerivativeTypeInput = (value: string): TradingStrategyLegDerivativeType => {
    switch (value) {
        case 'Option':
        case 'Future':
            return value;
        default:
            return null;
    }
};

export const parseTradingStrategyLegOptionTypeInput = (value: string): TradingStrategyLegFormData['optionType'] =>
    value === 'call' || value === 'put' ? value : null;

export const parseTradingStrategyLegEntryTransactionSizeTypeInput = (
    value: string,
    fallback: TradingStrategyLegEntryTransactionSizeType
): TradingStrategyLegEntryTransactionSizeType => {
    switch (value) {
        case 'percentageValue':
        case 'percentageRiskCapital':
        case 'percentageRiskToStop':
            return value;
        default:
            return fallback;
    }
};

export const parseTradingStrategyLegOpenTransactionTypeInput = (
    value: string,
    fallback: TradingStrategyLegOpenTransactionType
): TradingStrategyLegOpenTransactionType => {
    switch (value) {
        case 'buy':
        case 'shortSell':
        case 'buyToOpen':
        case 'sellToOpen':
            return value;
        default:
            return fallback;
    }
};

export const parseTradingStrategyNullablePositiveIntegerInput = (value: string): number | null => {
    const parsed = Number(value);

    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

export const parseTradingStrategyNonNegativeIntegerInput = (value: string, maxValue: number): number | undefined => {
    if (value === '') return undefined;

    const parsed = Number(value);

    if (!Number.isSafeInteger(parsed)) return undefined;

    return Math.max(0, Math.min(maxValue, parsed));
};

export const parseTradingStrategyPercentageInput = (value: string): number => {
    if (value === '') return 0;

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

export const coerceTradingStrategyLegOpenTransactionTypeForDerivativeType = (
    openTransactionType: TradingStrategyLegOpenTransactionType,
    derivativeType: TradingStrategyLegDerivativeType
): TradingStrategyLegOpenTransactionType => {
    const isShortOpenTransactionType = openTransactionType === 'shortSell' || openTransactionType === 'sellToOpen';

    if (derivativeType !== null) {
        return isShortOpenTransactionType ? 'sellToOpen' : 'buyToOpen';
    }

    return isShortOpenTransactionType ? 'shortSell' : 'buy';
};

export const buildTradingStrategyLegDerivativeTypeUpdate = (
    leg: TradingStrategyLegFormData,
    derivativeType: TradingStrategyLegDerivativeType
): Pick<
    TradingStrategyLegFormData,
    'autoBacktestOptionBuyingStrategiesOverride' | 'derivativeType' | 'openTransactionType' | 'optionType'
> => ({
    autoBacktestOptionBuyingStrategiesOverride: derivativeType === 'Option' ? leg.autoBacktestOptionBuyingStrategiesOverride : undefined,
    derivativeType,
    optionType: derivativeType === 'Option' && leg.derivativeType === 'Option' ? leg.optionType : null,
    openTransactionType: coerceTradingStrategyLegOpenTransactionTypeForDerivativeType(leg.openTransactionType, derivativeType)
});

export const getTradingStrategyLegSummary = (
    leg: Pick<TradingStrategyLegFormData, 'openTransactionType' | 'derivativeType' | 'optionType'>
): string => {
    const positionSide = getPositionSideFromOpenType(leg.openTransactionType);
    const ownershipLabel = positionSide === 'long' ? 'Long' : 'Short';
    const instrumentLabel =
        leg.derivativeType === 'Option' ? (leg.optionType ?? 'option') : leg.derivativeType === 'Future' ? 'future' : 'stock/crypto';
    const summary = `${ownershipLabel} ${instrumentLabel}`;
    const hasOptionAction = leg.openTransactionType === 'buyToOpen' || leg.openTransactionType === 'sellToOpen';

    // Unresolved options and unsupported action/instrument pairings keep exposure unknown.
    if (
        (leg.derivativeType === null ? hasOptionAction : !hasOptionAction) ||
        (leg.derivativeType === 'Option' ? leg.optionType === null : leg.optionType !== null)
    ) {
        return summary;
    }

    const { underlyingBias } = getStrategyLegSemantics(leg);
    return `${summary} · ${underlyingBias === 'bullish' ? 'Bullish' : 'Bearish'}`;
};

const buildCreateTradingStrategyLegInput = (
    formData: TradingStrategyFormData,
    strategyId: number,
    leg: TradingStrategyLegFormData,
    number: number
): CreateTradingStrategyLegInput => {
    const riskToStop = buildRiskToStopSizingFields(leg);

    return {
        ...buildTradingStrategyLegMutationFields(leg, number),
        // A new leg starts without a minimum, so an empty one is simply omitted.
        ...(riskToStop
            ? {
                  riskStopCaptureKey: riskToStop.riskStopCaptureKey,
                  ...(riskToStop.minimumQuantity !== null ? { minimumQuantity: riskToStop.minimumQuantity } : {})
              }
            : {}),
        description: leg.description || '',
        entityType: leg.derivativeType === 'Future' ? 'FuturesProduct' : formData.allowedUnderlyingEntityTypes[0] || 'Asset',
        strategyId
    };
};

const buildUpdateTradingStrategyLegInput = (
    leg: TradingStrategyLegFormData,
    number: number
): Omit<UpdateTradingStrategyLegInput, 'id'> => ({
    ...buildTradingStrategyLegMutationFields(leg, number),
    // An update always sends the minimum, so clearing the field persists as null instead of keeping the stored value.
    ...buildRiskToStopSizingFields(leg)
});

/** Only risk-to-stop sizing carries these; the backend clears both whenever a leg saves another size type. */
const buildRiskToStopSizingFields = (leg: TradingStrategyLegFormData): RiskToStopSizingFields | null =>
    leg.entryTransactionSizeType === 'percentageRiskToStop'
        ? { riskStopCaptureKey: leg.riskStopCaptureKey.trim(), minimumQuantity: leg.minimumQuantity }
        : null;

const buildTradingStrategyLegMutationFields = (leg: TradingStrategyLegFormData, number: number): StrategyLegMutationFields => ({
    autoBacktestOptionBuyingStrategiesOverride:
        leg.derivativeType === 'Option' ? leg.autoBacktestOptionBuyingStrategiesOverride : undefined,
    derivativeType: leg.derivativeType,
    description: leg.description,
    optionType: leg.optionType,
    entryTransactionSizeType: leg.entryTransactionSizeType,
    entryTransactionSizeValue: leg.entryTransactionSizeValue,
    name: leg.name,
    number,
    openTradeType: leg.openTransactionType
});

const buildTradingStrategyLegFormDataFromStrategy = (
    leg: TradingStrategyLeg,
    mode: TradingStrategyFormMode
): TradingStrategyLegFormData => ({
    ...(mode === 'edit' ? { id: leg.id } : {}),
    autoBacktestOptionBuyingStrategiesOverride: leg.autoBacktestOptionBuyingStrategiesOverride,
    derivativeType: leg.derivativeType || null,
    description: leg.description,
    optionType: leg.optionType,
    entryTransactionSizeType: leg.entryTransactionSizeType,
    entryTransactionSizeValue: leg.entryTransactionSizeValue,
    riskStopCaptureKey: leg.riskStopCaptureKey ?? '',
    minimumQuantity: leg.minimumQuantity ?? null,
    isDeleted: false,
    isModified: false,
    isNew: mode === 'copy',
    name: leg.name,
    openTransactionType: leg.openTransactionType
});

const toAllowedStrategyEntityTypes = (
    entityTypes: TradingStrategy['allowedUnderlyingEntityTypes']
): TradingStrategyFormData['allowedUnderlyingEntityTypes'] =>
    entityTypes.filter(
        (entityType): entityType is TradingStrategyFormData['allowedUnderlyingEntityTypes'][number] =>
            entityType === 'Asset' || entityType === 'Cryptocurrency' || entityType === 'AssetIndex' || entityType === 'FuturesProduct'
    );
